import * as React from "react";
import { create } from "zustand";

import type { TorsionPick } from "@/components/runs/torsion-selection";

/**
 * ONE fragment/torsion selection per run, shared by every surface that draws it
 * (flowstack#1223 E).
 *
 * The workspace (`RunWorkspace`) and the Analysis tab (`AnalysisTab`) render the
 * same fragment's torsions, from the same run, through the same
 * `DihedralRMSDGrid` — and each used to hold its OWN `useState` pick. So the two
 * tabs of one run disagreed about which torsion you were reading: the workspace
 * sat on `tor-0` while the Analysis tab, mounting fresh, resolved to the head of
 * the list and showed `tor-1`. Same run, same pills, two answers. Nothing on
 * either screen said a selection had changed, because from each component's own
 * point of view none had.
 *
 * Keyed by `run_id` rather than global: a selection is a fact about ONE run, and
 * carrying `frag-2`/`tor-1` into the next run opened would be the "looks right,
 * is not" defect one level up from the fragment/torsion nesting
 * (`torsion-selection.ts`). Readers still resolve what they read here against
 * the CURRENT list (`resolveFragmentId` / `resolveTorsionId`) — the store
 * remembers a pick, it does not certify that the pick still exists.
 *
 * NOT persisted, unlike `sidebar`/`theme`: this is where you were looking a
 * moment ago, not a preference, and a torsion id restored from `localStorage`
 * days later would name a torsion of a run that may since have been re-fitted.
 */
export interface RunSelection {
  /** `null` = nothing picked yet; the reader falls back to the first fragment. */
  fragmentId: string | null;
  /** The RAW pick — id AND the fragment it was made in. See `TorsionPick`. */
  torsionPick: TorsionPick | null;
}

const EMPTY_SELECTION: RunSelection = { fragmentId: null, torsionPick: null };

interface RunSelectionState {
  byRun: Record<string, RunSelection>;
  selectFragment: (runId: string, fragmentId: string | null) => void;
  selectTorsion: (runId: string, pick: TorsionPick) => void;
}

export const useRunSelectionStore = create<RunSelectionState>()((set) => ({
  byRun: {},
  selectFragment: (runId, fragmentId) =>
    set((s) => ({
      byRun: {
        ...s.byRun,
        [runId]: { ...(s.byRun[runId] ?? EMPTY_SELECTION), fragmentId },
      },
    })),
  selectTorsion: (runId, torsionPick) =>
    set((s) => ({
      byRun: {
        ...s.byRun,
        [runId]: { ...(s.byRun[runId] ?? EMPTY_SELECTION), torsionPick },
      },
    })),
}));

/**
 * Drop every remembered selection — for TESTS, and called once per test from
 * `src/test/setup.ts`.
 *
 * A module-level store outlives `cleanup()`, so without this a pick made by one
 * test is the starting state of the next and the second test passes or fails on
 * the first one's leftovers. That is the same cross-surface leak this store
 * exists to CREATE in the app, which is exactly why it has to be undone
 * between tests rather than tolerated.
 */
export function resetRunSelections(): void {
  useRunSelectionStore.setState({ byRun: {} });
}

/**
 * The run's selection plus the two setters, already bound to `runId`.
 *
 * The setters are `useCallback`-stable per run id so they can be handed
 * straight to a memoised child; the selection object is read with a plain
 * selector and falls back to a MODULE-LEVEL constant rather than a fresh
 * `{ fragmentId: null, torsionPick: null }`, which would be a new reference on
 * every render and defeat zustand's identity-based re-render check.
 */
export function useRunSelection(runId: string): RunSelection & {
  selectFragment: (fragmentId: string | null) => void;
  selectTorsion: (pick: TorsionPick) => void;
} {
  const selection = useRunSelectionStore((s) => s.byRun[runId] ?? EMPTY_SELECTION);
  const setFragment = useRunSelectionStore((s) => s.selectFragment);
  const setTorsion = useRunSelectionStore((s) => s.selectTorsion);
  const selectFragment = React.useCallback(
    (fragmentId: string | null) => setFragment(runId, fragmentId),
    [setFragment, runId]
  );
  const selectTorsion = React.useCallback(
    (pick: TorsionPick) => setTorsion(runId, pick),
    [setTorsion, runId]
  );
  return { ...selection, selectFragment, selectTorsion };
}
